import { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI, usersAPI, processingAPI, authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Settings,
  Clock,
  Building2,
  CalendarDays,
  UserCog,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  X,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Play,
  Loader2,
} from 'lucide-react';

const tabs = [
  { id: 'shifts',      label: 'Shifts',           icon: Clock        },
  { id: 'org',         label: 'Org Structure',     icon: Building2    },
  { id: 'assignments', label: 'User Assignments',  icon: UserCog      },
  { id: 'holidays',    label: 'Public Holidays',   icon: CalendarDays },
  { id: 'processing',  label: 'Processing',        icon: Play         },
];

const AdminPanel = () => {
  const { user, switchCompany } = useAuth();
  const [activeTab, setActiveTab] = useState('shifts');
  const [companies, setCompanies]     = useState([]);
  const [switching, setSwitching]     = useState(false);
  const [switchError, setSwitchError] = useState('');

  useEffect(() => {
    authAPI.getCompanies()
      .then(res => setCompanies(res.data.companies || []))
      .catch(() => {});
  }, []);

  const handleSwitch = async (companyId) => {
    if (companyId === user?.company_id) return;
    setSwitching(true);
    setSwitchError('');
    try {
      await switchCompany(companyId);
      window.location.reload();
    } catch (e) {
      setSwitchError('Switch failed');
      setSwitching(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="flex items-center mb-2">
        <Settings className="h-6 w-6 text-blue-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>

        {/* Company switcher */}
        {companies.length > 1 && (
          <div className="ml-auto flex items-center gap-2">
            {switchError && <span className="text-red-500 text-xs">{switchError}</span>}
            <span className="text-sm text-gray-500">Company:</span>
            <select
              value={user?.company_id ?? ''}
              onChange={e => handleSwitch(Number(e.target.value))}
              disabled={switching}
              className="border border-gray-300 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {user?.home_company_id && user.home_company_id !== user.company_id && (
              <button
                onClick={() => handleSwitch(user.home_company_id)}
                disabled={switching}
                className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              >
                Switch Back
              </button>
            )}
          </div>
        )}
      </div>

      <div className="border-b border-gray-200 bg-white shadow-sm rounded-t-lg px-4 pt-2">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`${activeTab === id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}>
              <Icon className="h-5 w-5 mr-2" />{label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'shifts'      && <ShiftsTab />}
        {activeTab === 'org'         && <OrgTab />}
        {activeTab === 'assignments' && <UserAssignmentsTab />}
        {activeTab === 'holidays'    && <HolidaysTab />}
        {activeTab === 'processing'  && <ProcessingTab />}
      </div>
    </div>
  );
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const Alert = ({ type, message, onClose }) => {
  const styles = type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700';
  const Icon = type === 'error' ? AlertCircle : CheckCircle;
  return (
    <div className={`border rounded-md p-3 flex items-start ${styles}`}>
      <Icon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
      <span className="text-sm flex-1">{message}</span>
      {onClose && <button onClick={onClose} className="ml-2"><X className="h-4 w-4" /></button>}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 overflow-y-auto z-50" role="dialog" aria-modal="true">
    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg w-full">
        <div className="px-6 pt-5 pb-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500"><X className="h-6 w-6" /></button>
          </div>
          {children}
        </div>
      </div>
    </div>
  </div>
);

const inputClass = 'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm';
const labelClass = 'block text-sm font-medium text-gray-700';

// ─── Shifts Tab ───────────────────────────────────────────────────────────────

const emptyShift = {
  code: '', name: '', start_time: '08:00', end_time: '17:00',
  is_night: false, buffer_start_mins: 30, buffer_end_mins: 30,
  grace_minutes: 0, is_active: true, friday_shift_id: '',
};

const ShiftsTab = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyShift);
  const [submitting, setSubmitting] = useState(false);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getShifts();
      setShifts(res.data.shifts);
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to load shifts' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const openCreate = () => { setForm(emptyShift); setModal({ mode: 'create' }); };
  const openEdit = (s) => {
    setForm({
      code: s.code, name: s.name,
      start_time: s.start_time.slice(0, 5), end_time: s.end_time.slice(0, 5),
      is_night: s.is_night, buffer_start_mins: s.buffer_start_mins,
      buffer_end_mins: s.buffer_end_mins, grace_minutes: s.grace_minutes,
      is_active: s.is_active, friday_shift_id: s.friday_shift_id || '',
    });
    setModal({ mode: 'edit', id: s.id });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, friday_shift_id: form.friday_shift_id || null };
      if (modal.mode === 'create') await adminAPI.createShift(payload);
      else await adminAPI.updateShift(modal.id, payload);
      setAlert({ type: 'success', message: `Shift ${modal.mode === 'create' ? 'created' : 'updated'}` });
      setModal(null);
      fetchShifts();
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Operation failed' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete shift "${s.name}"?`)) return;
    try {
      await adminAPI.deleteShift(s.id);
      setAlert({ type: 'success', message: 'Shift deleted' });
      fetchShifts();
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to delete shift' });
    }
  };

  const field = (name, val) => setForm(f => ({ ...f, [name]: val }));

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Shifts</h3>
        <div className="flex gap-2">
          <button onClick={fetchShifts} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</button>
          <button onClick={openCreate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Shift</button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No shifts configured yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Code','Name','Start','End','Night','Buf. Start','Buf. End','Grace','Friday Shift','Users','Active','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shifts.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-medium text-gray-900">{s.code}</td>
                    <td className="px-4 py-3 text-gray-900">{s.name}</td>
                    <td className="px-4 py-3 text-gray-700">{s.start_time.slice(0,5)}</td>
                    <td className="px-4 py-3 text-gray-700">{s.end_time.slice(0,5)}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${s.is_night ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}>{s.is_night ? 'Yes' : 'No'}</span></td>
                    <td className="px-4 py-3 text-gray-700">{s.buffer_start_mins}m</td>
                    <td className="px-4 py-3 text-gray-700">{s.buffer_end_mins}m</td>
                    <td className="px-4 py-3 text-gray-700">{s.grace_minutes}m</td>
                    <td className="px-4 py-3 text-gray-500">{s.friday_shift_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{s.user_count}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button onClick={() => openEdit(s)} className="text-blue-600 hover:text-blue-900 mr-3"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => handleDelete(s)} className="text-red-600 hover:text-red-900" disabled={s.user_count > 0} title={s.user_count > 0 ? 'Shift has assigned users' : ''}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal.mode === 'create' ? 'Add Shift' : 'Edit Shift'} onClose={() => setModal(null)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelClass}>Code</label><input className={inputClass} value={form.code} onChange={e => field('code', e.target.value)} required placeholder="e.g. DS01" /></div>
              <div><label className={labelClass}>Name</label><input className={inputClass} value={form.name} onChange={e => field('name', e.target.value)} required placeholder="e.g. Day Shift" /></div>
              <div><label className={labelClass}>Start Time</label><input type="time" className={inputClass} value={form.start_time} onChange={e => field('start_time', e.target.value)} required /></div>
              <div><label className={labelClass}>End Time</label><input type="time" className={inputClass} value={form.end_time} onChange={e => field('end_time', e.target.value)} required /></div>
              <div><label className={labelClass}>Buffer Start (mins)</label><input type="number" min="0" className={inputClass} value={form.buffer_start_mins} onChange={e => field('buffer_start_mins', parseInt(e.target.value) || 0)} required /></div>
              <div><label className={labelClass}>Buffer End (mins)</label><input type="number" min="0" className={inputClass} value={form.buffer_end_mins} onChange={e => field('buffer_end_mins', parseInt(e.target.value) || 0)} required /></div>
              <div><label className={labelClass}>Grace Minutes (flexi)</label><input type="number" min="0" className={inputClass} value={form.grace_minutes} onChange={e => field('grace_minutes', parseInt(e.target.value) || 0)} /></div>
              <div>
                <label className={labelClass}>Friday Shift (optional)</label>
                <select className={inputClass} value={form.friday_shift_id} onChange={e => field('friday_shift_id', e.target.value)}>
                  <option value="">— None —</option>
                  {shifts.filter(s => modal.mode === 'edit' ? s.id !== modal.id : true).map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={form.is_night} onChange={e => field('is_night', e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />Night Shift</label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={e => field('is_active', e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />Active</label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Saving…' : modal.mode === 'create' ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── Org Tab (4-level tree) ───────────────────────────────────────────────────

const OrgTab = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  // Expanded sets
  const [expandedDepts, setExpandedDepts] = useState(new Set());
  const [expandedSubdepts, setExpandedSubdepts] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());

  // Loaded children
  const [subdepts, setSubdepts] = useState({});
  const [sections, setSections] = useState({});
  const [subsections, setSubsections] = useState({});

  // Loading sets per level
  const [loadingNodes, setLoadingNodes] = useState(new Set());

  // Modal state
  const [modal, setModal] = useState(null);
  const [modalName, setModalName] = useState('');
  const [modalOtExempt, setModalOtExempt] = useState(false);
  const [modalOtApproveAll, setModalOtApproveAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchDepts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDepartments();
      setDepartments(res.data.departments);
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to load departments' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDepts(); }, [fetchDepts]);

  const setNodeLoading = (id, val) => setLoadingNodes(prev => { const s = new Set(prev); val ? s.add(id) : s.delete(id); return s; });

  const toggleDept = async (deptId) => {
    const next = new Set(expandedDepts);
    if (next.has(deptId)) { next.delete(deptId); setExpandedDepts(next); return; }
    next.add(deptId);
    setExpandedDepts(next);
    if (!subdepts[deptId]) {
      setNodeLoading(deptId, true);
      try { const res = await adminAPI.getSubdepts(deptId); setSubdepts(p => ({ ...p, [deptId]: res.data.subdepts })); }
      catch { setAlert({ type: 'error', message: 'Failed to load sub-departments' }); }
      finally { setNodeLoading(deptId, false); }
    }
  };

  const toggleSubdept = async (subdeptId) => {
    const next = new Set(expandedSubdepts);
    if (next.has(subdeptId)) { next.delete(subdeptId); setExpandedSubdepts(next); return; }
    next.add(subdeptId);
    setExpandedSubdepts(next);
    if (!sections[subdeptId]) {
      setNodeLoading(subdeptId, true);
      try { const res = await adminAPI.getSections(subdeptId); setSections(p => ({ ...p, [subdeptId]: res.data.sections })); }
      catch { setAlert({ type: 'error', message: 'Failed to load sections' }); }
      finally { setNodeLoading(subdeptId, false); }
    }
  };

  const toggleSection = async (sectionId) => {
    const next = new Set(expandedSections);
    if (next.has(sectionId)) { next.delete(sectionId); setExpandedSections(next); return; }
    next.add(sectionId);
    setExpandedSections(next);
    if (!subsections[sectionId]) {
      setNodeLoading(sectionId, true);
      try { const res = await adminAPI.getSubsections(sectionId); setSubsections(p => ({ ...p, [sectionId]: res.data.subsections })); }
      catch { setAlert({ type: 'error', message: 'Failed to load subsections' }); }
      finally { setNodeLoading(sectionId, false); }
    }
  };

  // Refresh helpers
  const refreshSubdepts = async (deptId) => { try { const res = await adminAPI.getSubdepts(deptId); setSubdepts(p => ({ ...p, [deptId]: res.data.subdepts })); } catch {} };
  const refreshSections = async (subdeptId) => { try { const res = await adminAPI.getSections(subdeptId); setSections(p => ({ ...p, [subdeptId]: res.data.sections })); } catch {} };
  const refreshSubsections = async (sectionId) => { try { const res = await adminAPI.getSubsections(sectionId); setSubsections(p => ({ ...p, [sectionId]: res.data.subsections })); } catch {} };

  const openModal = (config) => { setModal(config); setModalName(config.name || ''); setModalOtExempt(config.ot_restriction_exempt || false); setModalOtApproveAll(config.ot_approve_all || false); };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!modalName.trim()) return;
    setSubmitting(true);
    const name = modalName.trim();
    try {
      switch (modal.type) {
        case 'dept':
          if (modal.mode === 'create') await adminAPI.createDepartment({ name, ot_restriction_exempt: modalOtExempt, ot_approve_all: modalOtApproveAll });
          else await adminAPI.updateDepartment(modal.id, { name, ot_restriction_exempt: modalOtExempt, ot_approve_all: modalOtApproveAll });
          fetchDepts();
          break;
        case 'subdept':
          if (modal.mode === 'create') await adminAPI.createSubdept(modal.parentId, { name });
          else await adminAPI.updateSubdept(modal.id, { name });
          refreshSubdepts(modal.deptId);
          break;
        case 'section':
          if (modal.mode === 'create') await adminAPI.createSection(modal.parentId, { name });
          else await adminAPI.updateSection(modal.id, { name });
          refreshSections(modal.subdeptId);
          break;
        case 'subsection':
          if (modal.mode === 'create') await adminAPI.createSubsection(modal.parentId, { name });
          else await adminAPI.updateSubsection(modal.id, { name });
          refreshSubsections(modal.sectionId);
          break;
        default: break;
      }
      setAlert({ type: 'success', message: 'Saved successfully' });
      setModal(null);
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Operation failed' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (type, id, parentIds = {}) => {
    const labels = { dept: 'department', subdept: 'sub-department', section: 'section', subsection: 'subsection' };
    if (!window.confirm(`Delete this ${labels[type]}?`)) return;
    try {
      if (type === 'dept') { await adminAPI.deleteDepartment(id); fetchDepts(); }
      else if (type === 'subdept') { await adminAPI.deleteSubdept(id); refreshSubdepts(parentIds.deptId); }
      else if (type === 'section') { await adminAPI.deleteSection(id); refreshSections(parentIds.subdeptId); }
      else if (type === 'subsection') { await adminAPI.deleteSubsection(id); refreshSubsections(parentIds.sectionId); }
      setAlert({ type: 'success', message: 'Deleted successfully' });
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to delete' });
    }
  };

  const typeLabel = (modal) => {
    if (!modal) return '';
    const labels = { dept: 'Department', subdept: 'Sub-Department', section: 'Section', subsection: 'Subsection' };
    return `${modal.mode === 'create' ? 'Add' : 'Edit'} ${labels[modal.type]}`;
  };

  const rowBtn = (onClick, title, color = 'blue') => (
    <button onClick={onClick} className={`text-${color}-600 hover:text-${color}-900`} title={title}>
      {title === 'Delete' ? <Trash2 className="h-3.5 w-3.5" /> : <Edit2 className="h-3.5 w-3.5" />}
    </button>
  );

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Org Structure</h3>
        <div className="flex gap-2">
          <button onClick={fetchDepts} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</button>
          <button onClick={() => openModal({ type: 'dept', mode: 'create' })} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Department</button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : departments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No departments configured yet.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {departments.map(dept => (
              <div key={dept.id}>
                {/* Dept row */}
                <div className="flex items-center px-4 py-3 hover:bg-gray-50">
                  <button onClick={() => toggleDept(dept.id)} className="mr-2 text-gray-400 hover:text-gray-600">
                    {expandedDepts.has(dept.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <button onClick={() => toggleDept(dept.id)} className="flex-1 text-left">
                    <span className="font-medium text-gray-900">{dept.name}</span>
                    <span className="ml-3 text-xs text-gray-400">{dept.subdept_count} sub-dept{dept.subdept_count !== 1 ? 's' : ''} · {dept.user_count} user{dept.user_count !== 1 ? 's' : ''}</span>
                  </button>
                  <div className="flex items-center gap-3">
                    <button onClick={() => openModal({ type: 'subdept', mode: 'create', parentId: dept.id, deptId: dept.id })} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus className="h-3 w-3" /> Sub-dept</button>
                    {rowBtn(() => openModal({ type: 'dept', mode: 'edit', id: dept.id, name: dept.name, ot_restriction_exempt: dept.ot_restriction_exempt, ot_approve_all: dept.ot_approve_all }), 'Edit')}
                    {rowBtn(() => handleDelete('dept', dept.id), 'Delete', 'red')}
                  </div>
                </div>

                {/* Subdepts */}
                {expandedDepts.has(dept.id) && (
                  <div className="border-t border-gray-100">
                    {loadingNodes.has(dept.id) ? (
                      <div className="flex justify-center py-3"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" /></div>
                    ) : !subdepts[dept.id] || subdepts[dept.id].length === 0 ? (
                      <div className="px-12 py-2 text-sm text-gray-400 italic">No sub-departments</div>
                    ) : subdepts[dept.id].map(sd => (
                      <div key={sd.id}>
                        <div className="flex items-center px-10 py-2 hover:bg-gray-50 border-t border-gray-100">
                          <button onClick={() => toggleSubdept(sd.id)} className="mr-2 text-gray-400 hover:text-gray-600">
                            {expandedSubdepts.has(sd.id) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                          <button onClick={() => toggleSubdept(sd.id)} className="flex-1 text-left">
                            <span className="text-sm text-gray-800">{sd.name}</span>
                            <span className="ml-2 text-xs text-gray-400">{sd.section_count} section{sd.section_count !== 1 ? 's' : ''} · {sd.user_count} user{sd.user_count !== 1 ? 's' : ''}</span>
                          </button>
                          <div className="flex items-center gap-3">
                            <button onClick={() => openModal({ type: 'section', mode: 'create', parentId: sd.id, subdeptId: sd.id })} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus className="h-3 w-3" /> Section</button>
                            {rowBtn(() => openModal({ type: 'subdept', mode: 'edit', id: sd.id, deptId: dept.id, name: sd.name }), 'Edit')}
                            {rowBtn(() => handleDelete('subdept', sd.id, { deptId: dept.id }), 'Delete', 'red')}
                          </div>
                        </div>

                        {/* Sections */}
                        {expandedSubdepts.has(sd.id) && (
                          <div className="border-t border-gray-50">
                            {loadingNodes.has(sd.id) ? (
                              <div className="flex justify-center py-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /></div>
                            ) : !sections[sd.id] || sections[sd.id].length === 0 ? (
                              <div className="px-20 py-2 text-xs text-gray-400 italic">No sections</div>
                            ) : sections[sd.id].map(sec => (
                              <div key={sec.id}>
                                <div className="flex items-center px-16 py-2 hover:bg-gray-50 border-t border-gray-50">
                                  <button onClick={() => toggleSection(sec.id)} className="mr-2 text-gray-400 hover:text-gray-600">
                                    {expandedSections.has(sec.id) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  </button>
                                  <button onClick={() => toggleSection(sec.id)} className="flex-1 text-left">
                                    <span className="text-xs text-gray-700">{sec.name}</span>
                                    <span className="ml-2 text-xs text-gray-400">{sec.subsection_count} subsection{sec.subsection_count !== 1 ? 's' : ''} · {sec.user_count} user{sec.user_count !== 1 ? 's' : ''}</span>
                                  </button>
                                  <div className="flex items-center gap-3">
                                    <button onClick={() => openModal({ type: 'subsection', mode: 'create', parentId: sec.id, sectionId: sec.id })} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus className="h-2.5 w-2.5" /> Subsection</button>
                                    {rowBtn(() => openModal({ type: 'section', mode: 'edit', id: sec.id, subdeptId: sd.id, name: sec.name }), 'Edit')}
                                    {rowBtn(() => handleDelete('section', sec.id, { subdeptId: sd.id }), 'Delete', 'red')}
                                  </div>
                                </div>

                                {/* Subsections */}
                                {expandedSections.has(sec.id) && (
                                  <div>
                                    {loadingNodes.has(sec.id) ? (
                                      <div className="flex justify-center py-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" /></div>
                                    ) : !subsections[sec.id] || subsections[sec.id].length === 0 ? (
                                      <div className="px-24 py-1.5 text-xs text-gray-400 italic">No subsections</div>
                                    ) : subsections[sec.id].map(ss => (
                                      <div key={ss.id} className="flex items-center px-22 py-1.5 hover:bg-gray-50 border-t border-gray-50" style={{ paddingLeft: '5.5rem' }}>
                                        <span className="flex-1 text-xs text-gray-600 pl-5">{ss.name}</span>
                                        <span className="text-xs text-gray-400 mr-3">{ss.user_count} user{ss.user_count !== 1 ? 's' : ''}</span>
                                        {rowBtn(() => openModal({ type: 'subsection', mode: 'edit', id: ss.id, sectionId: sec.id, name: ss.name }), 'Edit')}
                                        <span className="ml-2">{rowBtn(() => handleDelete('subsection', ss.id, { sectionId: sec.id }), 'Delete', 'red')}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={typeLabel(modal)} onClose={() => setModal(null)}>
          <form onSubmit={handleModalSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Name</label>
              <input className={inputClass} value={modalName} onChange={e => setModalName(e.target.value)} required autoFocus />
            </div>
            {modal.type === 'dept' && (
              <>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="otExempt" checked={modalOtExempt} onChange={e => setModalOtExempt(e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                  <label htmlFor="otExempt" className="text-sm text-gray-700">Exempt from OT past-date restriction</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="otApproveAll" checked={modalOtApproveAll} onChange={e => setModalOtApproveAll(e.target.checked)} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                  <label htmlFor="otApproveAll" className="text-sm text-gray-700">Can approve all company OT (management)</label>
                </div>
              </>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Saving…' : modal.mode === 'create' ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── User Assignments Tab ─────────────────────────────────────────────────────

const UserAssignmentsTab = () => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDeptId, setFilterDeptId] = useState('');
  const [filterSubdeptId, setFilterSubdeptId] = useState('');
  const [filterSubdepts, setFilterSubdepts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk assign form
  const [bulkDeptId, setBulkDeptId] = useState('');
  const [bulkSubdepts, setBulkSubdepts] = useState([]);
  const [bulkSubdeptId, setBulkSubdeptId] = useState('');
  const [bulkSections, setBulkSections] = useState([]);
  const [bulkSectionId, setBulkSectionId] = useState('');
  const [bulkSubsections, setBulkSubsections] = useState([]);
  const [bulkSubsectionId, setBulkSubsectionId] = useState('');
  const [assigning, setAssigning] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, per_page: 20, show_inactive: false };
      if (searchTerm) params.search = searchTerm;
      if (filterDeptId) params.department_id = filterDeptId;
      if (filterSubdeptId) params.subdept_id = filterSubdeptId;
      const res = await usersAPI.getUsers(params);
      setUsers(res.data.users);
      setTotalPages(res.data.pages);
      setSelectedIds(new Set());
    } catch (e) {
      setAlert({ type: 'error', message: 'Failed to load users' });
    } finally { setLoading(false); }
  }, [currentPage, searchTerm, filterDeptId, filterSubdeptId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    usersAPI.getDepartments().then(r => setDepartments(r.data.departments)).catch(() => {});
  }, []);

  // Filter dept change — load subdepts for filter, reset subdept filter
  const onFilterDeptChange = async (deptId) => {
    setFilterDeptId(deptId);
    setFilterSubdeptId('');
    setFilterSubdepts([]);
    setCurrentPage(1);
    if (!deptId) return;
    try { const res = await usersAPI.getDeptSubdepts(deptId); setFilterSubdepts(res.data.subdepts); } catch {}
  };

  const onFilterSubdeptChange = (subdeptId) => {
    setFilterSubdeptId(subdeptId);
    setCurrentPage(1);
  };

  // Bulk dept cascading
  const onBulkDeptChange = async (deptId) => {
    setBulkDeptId(deptId);
    setBulkSubdeptId(''); setBulkSubdepts([]);
    setBulkSectionId(''); setBulkSections([]);
    setBulkSubsectionId(''); setBulkSubsections([]);
    if (!deptId) return;
    try { const res = await usersAPI.getDeptSubdepts(deptId); setBulkSubdepts(res.data.subdepts); } catch {}
  };

  const onBulkSubdeptChange = async (subdeptId) => {
    setBulkSubdeptId(subdeptId);
    setBulkSectionId(''); setBulkSections([]);
    setBulkSubsectionId(''); setBulkSubsections([]);
    if (!subdeptId) return;
    try { const res = await usersAPI.getSubdeptSections(subdeptId); setBulkSections(res.data.sections); } catch {}
  };

  const onBulkSectionChange = async (sectionId) => {
    setBulkSectionId(sectionId);
    setBulkSubsectionId(''); setBulkSubsections([]);
    if (!sectionId) return;
    try { const res = await usersAPI.getSectionSubsections(sectionId); setBulkSubsections(res.data.subsections); } catch {}
  };

  const handleBulkAssign = async () => {
    if (!bulkDeptId || selectedIds.size === 0) return;
    setAssigning(true);
    try {
      const updates = Array.from(selectedIds).map(userId => ({
        user_id: userId,
        department_id: parseInt(bulkDeptId),
        subdept_id: bulkSubdeptId ? parseInt(bulkSubdeptId) : null,
        section_id: bulkSectionId ? parseInt(bulkSectionId) : null,
        subsection_id: bulkSubsectionId ? parseInt(bulkSubsectionId) : null,
      }));
      await usersAPI.batchUpdateUsers({ updates });
      setAlert({ type: 'success', message: `Department assigned to ${selectedIds.size} user(s)` });
      setSelectedIds(new Set());
      setBulkDeptId(''); setBulkSubdeptId(''); setBulkSectionId(''); setBulkSubsectionId('');
      setBulkSubdepts([]); setBulkSections([]); setBulkSubsections([]);
      fetchUsers();
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to assign department' });
    } finally { setAssigning(false); }
  };

  const toggleAll = () => {
    setSelectedIds(selectedIds.size === users.length ? new Set() : new Set(users.map(u => u.id)));
  };

  const toggle = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const selectEl = 'block w-full border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">User Department Assignments</h3>
        <button onClick={fetchUsers} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder="Search name or staff no…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <select value={filterDeptId} onChange={e => onFilterDeptChange(e.target.value)}
          className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {filterDeptId && (
          <select value={filterSubdeptId} onChange={e => onFilterSubdeptChange(e.target.value)}
            className="border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            <option value="">All Sub-Depts</option>
            <option value="0">— No Sub-Department —</option>
            {filterSubdepts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {(searchTerm || filterDeptId || filterSubdeptId) && (
          <button onClick={() => { setSearchTerm(''); onFilterDeptChange(''); setCurrentPage(1); }} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"><X className="h-4 w-4" /> Clear</button>
        )}
      </div>

      {/* Bulk assign bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900"><span className="font-bold">{selectedIds.size}</span> user{selectedIds.size !== 1 ? 's' : ''} selected</span>
            <button onClick={() => setSelectedIds(new Set())} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"><X className="h-3 w-3" /> Clear selection</button>
          </div>
          <p className="text-xs text-blue-700">Only levels you select will be updated. Lower levels are cleared when a higher level is assigned.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Department <span className="text-red-500">*</span></label>
              <select value={bulkDeptId} onChange={e => onBulkDeptChange(e.target.value)} className={selectEl}>
                <option value="">— Select —</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Sub-Department</label>
              <select value={bulkSubdeptId} onChange={e => onBulkSubdeptChange(e.target.value)} disabled={!bulkSubdepts.length} className={`${selectEl} disabled:bg-gray-100 disabled:text-gray-400`}>
                <option value="">— None —</option>
                {bulkSubdepts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Section</label>
              <select value={bulkSectionId} onChange={e => onBulkSectionChange(e.target.value)} disabled={!bulkSections.length} className={`${selectEl} disabled:bg-gray-100 disabled:text-gray-400`}>
                <option value="">— None —</option>
                {bulkSections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-blue-800 mb-1">Subsection</label>
              <select value={bulkSubsectionId} onChange={e => setBulkSubsectionId(e.target.value)} disabled={!bulkSubsections.length} className={`${selectEl} disabled:bg-gray-100 disabled:text-gray-400`}>
                <option value="">— None —</option>
                {bulkSubsections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={handleBulkAssign} disabled={!bulkDeptId || assigning} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {assigning ? 'Assigning…' : 'Assign Department'}
            </button>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No users found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input type="checkbox" checked={selectedIds.size === users.length && users.length > 0} ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < users.length; }}
                        onChange={toggleAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </th>
                    {['Name', 'Staff No', 'Department', 'Sub-dept', 'Section', 'Subsection'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map(u => (
                    <tr key={u.id} className={`hover:bg-gray-50 ${selectedIds.has(u.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggle(u.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" /></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{u.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">{u.staff_no}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">{u.department_name || <span className="text-red-400 text-xs">Unassigned</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{u.subdept_name || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{u.section_name || <span className="text-gray-300">—</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{u.subsection_name || <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <p className="text-sm text-gray-700">Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span></p>
                <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"><ChevronRightIcon className="h-5 w-5" /></button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Public Holidays Tab ──────────────────────────────────────────────────────

const HolidaysTab = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ date: '', name: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getHolidays({ year });
      setHolidays(res.data.holidays);
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to load holidays' });
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const openCreate = () => { setForm({ date: `${year}-01-01`, name: '' }); setModal({ mode: 'create' }); };
  const openEdit = (h) => { setForm({ date: h.date, name: h.name }); setModal({ mode: 'edit', id: h.id }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (modal.mode === 'create') await adminAPI.createHoliday(form);
      else await adminAPI.updateHoliday(modal.id, form);
      setAlert({ type: 'success', message: `Holiday ${modal.mode === 'create' ? 'added' : 'updated'}` });
      setModal(null);
      fetchHolidays();
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Operation failed' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    try {
      await adminAPI.deleteHoliday(h.id);
      setAlert({ type: 'success', message: 'Holiday deleted' });
      fetchHolidays();
    } catch (e) {
      setAlert({ type: 'error', message: e.response?.data?.error || 'Failed to delete' });
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 1 + i);
  const formatDate = (iso) => { const [y, m, d] = iso.split('-'); return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); };
  const dayName = (iso) => { const [y, m, d] = iso.split('-'); return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'long' }); };

  return (
    <div className="space-y-4">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">Public Holidays</h3>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border border-gray-300 rounded-md py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500">
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchHolidays} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</button>
          <button onClick={openCreate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Holiday</button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : holidays.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No public holidays for {year}.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>{['#','Date','Day','Holiday Name','Actions'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {holidays.map((h, i) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{formatDate(h.date)}</td>
                  <td className="px-4 py-3 text-gray-500">{dayName(h.date)}</td>
                  <td className="px-4 py-3 text-gray-900">{h.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button onClick={() => openEdit(h)} className="text-blue-600 hover:text-blue-900 mr-3"><Edit2 className="h-4 w-4" /></button>
                    <button onClick={() => handleDelete(h)} className="text-red-600 hover:text-red-900"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <Modal title={modal.mode === 'create' ? 'Add Holiday' : 'Edit Holiday'} onClose={() => setModal(null)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required />
            </div>
            <div>
              <label className={labelClass}>Holiday Name</label>
              <input className={inputClass} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required autoFocus placeholder="e.g. New Year's Day" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Saving…' : modal.mode === 'create' ? 'Add Holiday' : 'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

// ─── Processing Tab ───────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);

const ProcessingTab = () => {
  const [startDate, setStartDate]           = useState(todayStr());
  const [endDate, setEndDate]               = useState(todayStr());
  const [staffNo, setStaffNo]               = useState('');
  const [force, setForce]                   = useState(false);
  const [companies, setCompanies]           = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [running, setRunning]               = useState(false);
  const [job, setJob]                       = useState(null);   // { job_id, status, result, message }
  const [alert, setAlert]                   = useState(null);
  const pollRef                             = useRef(null);

  useEffect(() => {
    processingAPI.getCompanies()
      .then(res => setCompanies(res.data))
      .catch(() => {});
  }, []);

  const clearPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = async (jobId) => {
    try {
      const res = await processingAPI.getJobStatus(jobId);
      const data = res.data;
      setJob(data);
      if (data.status === 'done' || data.status === 'error') {
        clearPoll();
        setRunning(false);
        if (data.status === 'error') {
          setAlert({ type: 'error', message: data.message || 'Processing failed' });
        }
      }
    } catch (e) {
      clearPoll();
      setRunning(false);
      setAlert({ type: 'error', message: 'Failed to poll job status' });
    }
  };

  const handleRun = async (e) => {
    e.preventDefault();
    if (!startDate || !endDate) return;
    setAlert(null);
    setJob(null);
    setRunning(true);
    try {
      const payload = { start_date: startDate, end_date: endDate, force };
      if (staffNo.trim()) payload.staff_no = staffNo.trim();
      if (selectedCompanyId) payload.company_id = parseInt(selectedCompanyId);
      const res = await processingAPI.processAllPending(payload);
      const { job_id } = res.data;
      setJob({ job_id, status: 'running' });
      // Poll every 2 seconds
      const id = setInterval(() => pollJob(job_id), 2000);
      pollRef.current = id;
    } catch (err) {
      setRunning(false);
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to start processing' });
    }
  };

  const BADGE_CLASSES = {
    blue:   { wrap: 'bg-blue-50 border-blue-200',     val: 'text-blue-700',   lbl: 'text-blue-600'   },
    green:  { wrap: 'bg-green-50 border-green-200',   val: 'text-green-700',  lbl: 'text-green-600'  },
    red:    { wrap: 'bg-red-50 border-red-200',       val: 'text-red-700',    lbl: 'text-red-600'    },
    yellow: { wrap: 'bg-yellow-50 border-yellow-200', val: 'text-yellow-700', lbl: 'text-yellow-600' },
    gray:   { wrap: 'bg-gray-50 border-gray-200',     val: 'text-gray-700',   lbl: 'text-gray-600'   },
  };
  const resultBadge = (label, value, color = 'gray') => {
    const c = BADGE_CLASSES[color] || BADGE_CLASSES.gray;
    return (
      <div className={`flex flex-col items-center px-4 py-3 rounded-lg border ${c.wrap}`}>
        <span className={`text-2xl font-bold ${c.val}`}>{value ?? '—'}</span>
        <span className={`text-xs ${c.lbl} mt-0.5`}>{label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="bg-white shadow rounded-lg p-6 space-y-5">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Reprocess Records</h3>
          <p className="text-sm text-gray-500 mt-1">
            Runs Raw → Staging → Final for the selected date range. Unprocessed raw records
            in the range are transformed; the resulting batch is then processed into final records.
            Existing staging records for the same user/workday are updated (not duplicated).
          </p>
        </div>

        <form onSubmit={handleRun} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" className={inputClass} value={startDate}
                onChange={e => setStartDate(e.target.value)} required disabled={running} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="date" className={inputClass} value={endDate}
                onChange={e => setEndDate(e.target.value)} required disabled={running} />
            </div>
            <div>
              <label className={labelClass}>Staff No (optional)</label>
              <input className={inputClass} value={staffNo} placeholder="Leave blank for all staff"
                onChange={e => setStaffNo(e.target.value)} disabled={running} />
            </div>
            <div>
              <label className={labelClass}>Entity (optional)</label>
              <select className={inputClass} value={selectedCompanyId}
                onChange={e => setSelectedCompanyId(e.target.value)} disabled={running}>
                <option value="">All entities</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)}
                disabled={running} className="h-4 w-4 text-orange-600 border-gray-300 rounded" />
              <span>Force reprocess <span className="text-gray-400 font-normal">(re-runs already-processed records)</span></span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={running}
              className={`inline-flex items-center px-5 py-2 border border-transparent text-sm font-medium rounded-md text-white disabled:opacity-50 ${force ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {running
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing…</>
                : <><Play className="h-4 w-4 mr-2" /> Run</>}
            </button>
            {job && job.status === 'running' && (
              <span className="text-sm text-gray-500">Job {job.job_id?.slice(0, 8)}… running</span>
            )}
          </div>
        </form>
      </div>

      {job && job.status === 'done' && job.result && (
        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Results</h4>

          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase">Transformation (Raw → Staging)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {job.result.transformation?.reset_count > 0 && resultBadge('Raw reset', job.result.transformation.reset_count, 'yellow')}
              {resultBadge('Groups processed', job.result.transformation?.processed, 'blue')}
              {resultBadge('Errors', job.result.transformation?.errors, job.result.transformation?.errors > 0 ? 'red' : 'gray')}
              {resultBadge('Batch ID', job.result.transformation?.batch_id?.slice(0, 8) + '…', 'gray')}
            </div>
            {job.result.transformation?.message && (
              <p className="text-sm text-gray-500">{job.result.transformation.message}</p>
            )}
            {job.result.transformation?.error_details?.length > 0 && (
              <details className="mt-1">
                <summary className="text-xs text-red-600 cursor-pointer hover:underline">
                  Show {job.result.transformation.error_details.length} error detail{job.result.transformation.error_details.length !== 1 ? 's' : ''}
                </summary>
                <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                  {job.result.transformation.error_details.map((e, i) => (
                    <li key={i} className="text-xs font-mono text-red-700 bg-red-50 px-2 py-1 rounded">{e}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>

          {(() => {
            const raw = job.result.processing || {};
            const totals = Object.values(raw).reduce(
              (acc, r) => ({ processed: acc.processed + (r.processed || 0), errors: acc.errors + (r.errors || 0), exceptions: acc.exceptions + (r.exceptions || 0) }),
              { processed: 0, errors: 0, exceptions: 0 }
            );
            return (
              <div className="space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase">Processing (Staging → Final)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {resultBadge('Records processed', totals.processed, 'green')}
                  {resultBadge('Errors', totals.errors, totals.errors > 0 ? 'red' : 'gray')}
                  {resultBadge('Exceptions', totals.exceptions, totals.exceptions > 0 ? 'yellow' : 'gray')}
                </div>
                {Object.keys(raw).length > 1 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 pt-1">
                    {Object.entries(raw).map(([cid, r]) => {
                      const co = companies.find(c => String(c.id) === String(cid));
                      return (
                        <div key={cid} className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2">
                          <p className="font-medium text-gray-700 truncate">{co?.name ?? `Entity ${cid}`}</p>
                          <p className="text-gray-500 mt-0.5">{r.processed ?? 0} processed · {r.errors ?? 0} err · {r.exceptions ?? 0} exc</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
